import { getArtigo, type Artigo } from "@/lib/artigo.functions";
import {
  breadcrumbSchema,
  faqSchema,
  medicalWebPageSchema,
  type JsonLdObject,
} from "@/lib/schema";
import { buildHead } from "@/lib/seo";
import { getSiteData, type SiteData } from "@/lib/site-data.functions";

export type DadosArtigo = {
  artigo: Artigo;
  doctor: SiteData["doctor"];
  bio: string | null;
  meta: Array<Record<string, string>>;
  trilha: Array<{ nome: string; path: string }>;
  schemas: JsonLdObject[];
};

/**
 * Carrega um artigo publicado no servidor. Devolve null quando o slug não é um
 * artigo publicado, para quem chama decidir entre outra página e o 404.
 */
export async function carregarArtigo({
  slug,
  path,
}: {
  slug: string;
  path: string;
}): Promise<DadosArtigo | null> {
  // Artigo e dados do site em paralelo: uma ida ao banco a menos no caminho.
  const [artigo, site] = await Promise.all([getArtigo({ data: { slug } }), getSiteData()]);
  if (!artigo) return null;
  const titulo = artigo.h1 || artigo.title || "";

  const head = await buildHead({
    data: {
      path,
      fallback: {
        title: artigo.seo_title || `${titulo} | Dr. Thiago Viana`,
        ...(artigo.meta_description ? { description: artigo.meta_description } : {}),
      },
    },
  });

  // Canonical, robots e o restante do Open Graph saem uma única vez do layout
  // raiz. Aqui ficam só title e descrição, que são próprios da página.
  const proprios = new Set(["title", "description", "og:title", "og:description"]);
  const meta = head.meta.filter(
    (tag) => proprios.has(tag["name"] ?? tag["property"] ?? "") || "title" in tag,
  );

  const trilha: Array<{ nome: string; path: string }> = [
    { nome: "Home", path: "/" },
    { nome: "Blog", path: "/blog/" },
  ];
  if (artigo.categoria) {
    trilha.push({ nome: artigo.categoria.label, path: `/blog/${artigo.categoria.slug}/` });
  }

  const schemas: JsonLdObject[] = [
    medicalWebPageSchema({
      path,
      title: titulo,
      description: artigo.meta_description,
      datePublished: artigo.published_at,
      // dateModified só existe porque a data de revisão aparece na assinatura.
      dateModified: artigo.reviewed_at,
    }),
    breadcrumbSchema(trilha),
  ];

  // FAQPage só quando as perguntas aparecem na página.
  const faq = faqSchema(artigo.faq);
  if (faq) schemas.push(faq);

  return {
    artigo,
    doctor: site.doctor,
    bio: site.doctor?.bio_short ?? null,
    meta,
    trilha,
    schemas,
  };
}
